import { ReactElement, useState } from 'react'

import Dropdown from 'components/v2/Dropdown'
import { DotsIcon } from 'components/Icons/DotsIcon'
import IconButton from 'components/v2/IconButton'
import List from 'components/v2/List'
import { GTKredit, GTKreditAccountType } from 'gql/types.generated'
import { FormattedMessage } from 'react-intl'
import { WarningIcon } from 'components/Icons'
import { CoinIcon } from 'components/Icons/CoinIcon'
import styled from 'styled-components'
import { ApolloError, useMutation, useQuery } from '@apollo/client'
import Modal from 'components/v2/Modal'

import { DEPOSIT_KREDITS_CORPORATE_FUNDS, DEPOSIT_KREDITS_BONUS_FUNDS } from 'features/Kredits/mutations-legacy'
import { IDepositKreditsFormValues } from 'features/Kredits/types'
import { DepositKreditsForm } from '../../../components/DepositKreditsForm'
import { GET_BUSINESS_CLIENT_KREDITS_SETTINGS, GET_KREDITS } from 'features/Kredits/queries-legacy'
import { isEmpty } from 'lodash'
import { getQueries } from 'utils/route'
import { sanitizePrice } from 'utils/math'
import { useLogin } from 'features/Login/hooks'
import { canDepositKreditsCorporateFund } from 'features/Users/utils'
import { GET_BUSINESS_SITE_KREDITS_SETTINGS } from 'features/Kredits/queries'

const StyledWarningIcon = styled(WarningIcon)`
  margin-left: 10px;
`

interface KreditsMultipleActionsProps {
  selectedItems: GTKredit[]
}

export const KreditsMultipleActions = ({ selectedItems }: KreditsMultipleActionsProps): ReactElement | null => {
  const login = useLogin()
  const [isDepositKreditsModalOpen, setIsDepositKreditsModalOpen] = useState(false)
  const { clientId = '', siteId = '' } = getQueries(location.search)
  const { data: businessClientKreditsSettingsData } = useQuery(GET_BUSINESS_CLIENT_KREDITS_SETTINGS, {
    variables: { id: clientId },
    skip: isEmpty(clientId)
  })
  const { data: businessSiteKreditsSettingsData } = useQuery(GET_BUSINESS_SITE_KREDITS_SETTINGS, {
    variables: { id: siteId },
    skip: isEmpty(siteId)
  })
  const [depositCorporateKredits, { error: depositCorporateKreditsError, loading: depositCorporateKreditsLoading }] =
    useMutation(DEPOSIT_KREDITS_CORPORATE_FUNDS, {
      onCompleted: (): void => {
        setIsDepositKreditsModalOpen(false)
      },
      onError: (): void => {
        // Do nothing
      },
      refetchQueries: [GET_KREDITS]
    })
  const [depositBonusKredits, { error: depositBonusKreditsError, loading: depositBonusKreditsLoading }] = useMutation(
    DEPOSIT_KREDITS_BONUS_FUNDS,
    {
      onCompleted: (): void => {
        setIsDepositKreditsModalOpen(false)
      },
      onError: (): void => {
        // Do nothing
      },
      refetchQueries: [GET_KREDITS]
    }
  )

  const isDepositKreditsDisabled =
    !canDepositKreditsCorporateFund(login.accessToken.policies) ||
    selectedItems.length === 0 ||
    (isEmpty(businessClientKreditsSettingsData?.businessClient.kreditsSettings) &&
      isEmpty(businessSiteKreditsSettingsData?.businessSite.kreditsSettings))

  const [isOpenDropdown, setIsOpenDropdown] = useState(false)
  const toggleDropdown = (): void => {
    setIsOpenDropdown(!isOpenDropdown)
  }
  const onSubmit = (values: IDepositKreditsFormValues): void => {
    switch (values.creditType) {
      case GTKreditAccountType.Corporate:
        depositCorporateKredits({
          variables: {
            input: {
              accounts: selectedItems.map((kredit) => ({
                kreditID: kredit.id,
                userAccountID: kredit.user.id
              })),
              amount: sanitizePrice(Number(values.amount) * 100)
            }
          }
        })
        break
      case GTKreditAccountType.Bonus:
        depositBonusKredits({
          variables: {
            input: {
              accounts: selectedItems.map((kredit) => ({
                kreditID: kredit.id,
                userAccountID: kredit.user.id
              })),
              amount: sanitizePrice(Number(values.amount) * 100)
            }
          }
        })
        break
    }
  }
  return (
    <>
      <Dropdown.Wrapper noPadding noIndicator right onClickAway={(): void => setIsOpenDropdown(false)}>
        <IconButton onClick={toggleDropdown} icon={<DotsIcon />} noBorder smallPadding />
        <Dropdown.Content open={isOpenDropdown} noIndicator left noOverflow>
          <List.Container>
            <List.Item action noPadding>
              <List.Item
                action
                onClick={(): void => {
                  if (!isDepositKreditsDisabled) {
                    setIsDepositKreditsModalOpen(true)
                  }
                }}
                disabled={isDepositKreditsDisabled}
              >
                <List.Icon>
                  <CoinIcon />
                </List.Icon>
                <FormattedMessage id="kredits.multipleActions.addCredits" defaultMessage="Add Credits" />
                {depositCorporateKreditsError || depositBonusKreditsError ? <StyledWarningIcon /> : null}
              </List.Item>
            </List.Item>
          </List.Container>
        </Dropdown.Content>
      </Dropdown.Wrapper>
      {isDepositKreditsModalOpen ? (
        <Modal.Container
          title={
            <FormattedMessage id="kredits.multipleActions.depositKreditsModal.title" defaultMessage="Deposit Kredits" />
          }
          onClose={(): void => setIsDepositKreditsModalOpen(false)}
        >
          <DepositKreditsForm
            onSubmit={onSubmit}
            error={depositCorporateKreditsError || depositBonusKreditsError}
            onCancel={(): void => setIsDepositKreditsModalOpen(false)}
            loading={depositCorporateKreditsLoading || depositBonusKreditsLoading}
          />
        </Modal.Container>
      ) : null}
    </>
  )
}

export interface IDepositKreditsFormProps {
  onSubmit: (values: IDepositKreditsFormValues) => void
  onCancel: () => void
  loading: boolean
  error?: ApolloError
}
